import { Action } from 'redux';
import { Metadata, Schema } from '../../types/Metadata';
import { ValidationResponse } from '../../types/ValidationResponse';
import { ValidationMode } from '../../enums/ValidationMode';
import { ItemType } from '../../enums/ItemType';

export interface MetadataFormState {
  reqNum?: number;
  data?: Partial<Metadata>;
  original?: Partial<Metadata>;
  schema?: Schema;
  autoAccepted?: boolean;
  shouldValidate?: ValidationMode;
  validateInProgressMsg?: string;
  approvalInProgress?: boolean;
  pushResponse?: ValidationResponse;
  fieldErrors?: ValidationResponse;
  comments?: any[];
  shouldShowComments?: boolean;
  shouldShowMetrics?: boolean;
  allErrorFields?: Set<string>;
  showConfigureWorkflowModal?: boolean;
}

const UPDATE_METADATA_FORM_STATE = 'UPDATE_METADATA_FORM_STATE';
const UPDATE_METADATA_FORM_DATA = 'UPDATE_METADATA_FORM_DATA';

const UPDATE_SHOULD_VALIDATE = 'UPDATE_SHOULD_VALIDATE';

const UPDATE_METADATA_FORM_STATE_FIELD = 'UPDATE_METADATA_FORM_STATE_FIELD';

const UPDATE_APPROVAL_STATE = 'UPDATE_APPROVAL_STATE';
const UPDATE_METADATA_FORM_FIELD_ERRORS = 'UPDATE_METADATA_FORM_FIELD_ERRORS';
const UPDATE_ALL_ERROR_FIELDS = 'UPDATE_ALL_ERROR_FIELDS';

export const updateMetadataFormData = (
  newState: Partial<MetadataFormState>
) => ({
  type: UPDATE_METADATA_FORM_DATA,
  ...newState
});

export const updateMetadataFormState = (
  newState: Partial<MetadataFormState>
) => ({
  type: UPDATE_METADATA_FORM_STATE,
  ...newState
});

export const updateApprovalState = (
  newState: Pick<MetadataFormState, 'approvalInProgress'>
) => ({
  type: UPDATE_APPROVAL_STATE,
  ...newState
});

export const updateShouldValidate = (
  newState: Pick<MetadataFormState, 'shouldValidate' | 'validateInProgressMsg'>
) => ({
  type: UPDATE_SHOULD_VALIDATE,
  ...newState
});

export const updateMetadataFormStateField = (
  newState: Partial<MetadataFormState>
) => ({
  type: UPDATE_METADATA_FORM_STATE_FIELD,
  ...newState
});

export const updateFieldErrors = (fieldErrors: ValidationResponse) => ({
  type: UPDATE_METADATA_FORM_FIELD_ERRORS,
  fieldErrors
});

export const updateAllErrorFields = (allErrorFields: Set<string>) => ({
  type: UPDATE_ALL_ERROR_FIELDS,
  allErrorFields
});

type FormAction = Action & Partial<MetadataFormState>;

const reducer = (
  state: MetadataFormState = {},
  action: FormAction
): MetadataFormState => {
  const { type, ...newState } = action;
  switch (type) {
    case UPDATE_METADATA_FORM_FIELD_ERRORS:
      return { ...state, fieldErrors: action.fieldErrors };
    case UPDATE_METADATA_FORM_DATA:
      if (newState.reqNum && state.data && newState.data) {
        const { reqNum, data } = newState;
        if (
          state.data?.props?.refId == data?.props?.refId &&
          state.reqNum <= reqNum
        ) {
          return { ...state, reqNum, data, original: data };
        }
      }
      return state;
    case UPDATE_METADATA_FORM_STATE:
      if (newState.reqNum && state.data) {
        const { reqNum, original } = newState;
        if (original && state.reqNum <= reqNum) {
          const data = state.data;
          const { metadata, props, approvals, myRoles } = original;
          if (metadata) data.metadata = metadata;
          data.approvals = approvals;
          data.myRoles = myRoles;
          if (props) {
            const fieldsToUpdate = [
              'modifiedBy',
              'taskAssignees',
              'daysInCurrentStatus',
              'statusStartedDateTime',
              'daysInCurrentDesignStatus',
              'designStatusStartedDateTime',
              'configureWorkflowType',
              'configureLightUpdateWorkflowType',
              'configureWorkflowByEmpCd',
              'configureWorkflowSkipIds',
              'configureLightUpdateWorkflowSkipIds',
              'requestCreativeHelp',
              'bigRocks',
              'shouldAutocheckAndDisableBigRocks',
              'peerReviewer'
            ];
            if (props.itemType == ItemType.content) {
              fieldsToUpdate.push(
                ...[
                  'additionalFunctions',
                  'primaryPriorityJourneyStep',
                  'priorityJourneyStep',
                  'additionalRoles',
                  'primaryRolePriority',
                  'rolePriority',
                  'additionalTaxonomyTag',
                  'taxonomyTag',
                  'topicCategory',
                  'additionalTopicCategory'
                ]
              );
              if (!!props.itemId) {
                fieldsToUpdate.push(...['function', 'role']);
              }
            }
            let updated = false;
            fieldsToUpdate.forEach(f => {
              data.props[f] = props[f];
              updated = true;
            });
            if (updated) state.data = { ...data };
          }
        }
      } else if (!newState.schema && !newState.data) {
        return {};
      }
      return { ...state, ...newState };
    case UPDATE_SHOULD_VALIDATE:
      return { ...state, ...newState };
    case UPDATE_APPROVAL_STATE:
      return { ...state, ...newState };
    case UPDATE_METADATA_FORM_STATE_FIELD:
      return { ...state, ...newState };
    case UPDATE_ALL_ERROR_FIELDS:
      return { ...state, allErrorFields: action.allErrorFields };
    default:
      return state;
  }
};

export default reducer;
